import React, { useState, useMemo } from "react";
import { ChevronDown, Check, Plus, X } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from "./ui/command";
import { Button } from "./ui/button";
import { Input } from "./ui/input";

/**
 * SearchableSelect — Combobox built on shadcn's Command + Popover primitives.
 *
 * Props:
 *  - value: string
 *  - onChange: (val: string) => void
 *  - options: string[]               (curated list)
 *  - customOptions?: string[]        (user-added pairs — rendered above options, prefixed with a "+" tag)
 *  - placeholder?: string
 *  - searchPlaceholder?: string
 *  - emptyText?: string
 *  - showAddCustom?: boolean         (renders a "+ Add Pair" footer)
 *  - onAddCustom?: (val: string) => void
 *  - addCustomLabel?: string         (defaults to "Add Pair")
 *  - testId?: string                 (root trigger button data-testid)
 *  - className?: string              (trigger className)
 *  - triggerHeight?: string          (defaults to h-11)
 */
export function SearchableSelect({
  value,
  onChange,
  options,
  customOptions = [],
  placeholder = "Select…",
  searchPlaceholder = "Search…",
  emptyText = "No matches",
  showAddCustom = false,
  onAddCustom,
  addCustomLabel = "Add Pair",
  testId,
  className = "",
  triggerHeight = "h-11",
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [addingCustom, setAddingCustom] = useState(false);
  const [customInput, setCustomInput] = useState("");

  // Dedupe + put customOptions on top.
  const merged = useMemo(() => {
    const seen = new Set();
    const out = [];
    for (const o of customOptions) {
      const k = String(o || "").trim();
      if (k && !seen.has(k)) { seen.add(k); out.push({ value: k, custom: true }); }
    }
    for (const o of options) {
      const k = String(o || "").trim();
      if (k && !seen.has(k)) { seen.add(k); out.push({ value: k, custom: false }); }
    }
    return out;
  }, [options, customOptions]);

  const select = (v) => {
    onChange(v);
    setQuery("");
    setOpen(false);
  };

  const handleAddCustom = () => {
    const v = customInput.trim().toUpperCase();
    if (!v) return;
    if (typeof onAddCustom === "function") onAddCustom(v);
    onChange(v);
    setCustomInput("");
    setAddingCustom(false);
    setQuery("");
    setOpen(false);
  };

  // Keep the popover sized to its trigger.
  const triggerCls =
    `w-full justify-between bg-transparent border-[var(--bt-border)] data-[placeholder]:text-[var(--bt-text-muted)] focus:border-[var(--bt-accent)] focus:ring-1 focus:ring-[var(--bt-accent)] ${triggerHeight} rounded-sm font-normal ${className}`;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          data-testid={testId}
          className={triggerCls}
        >
          <span className={value ? "" : "text-[var(--bt-text-muted)]"}>
            {value || placeholder}
          </span>
          <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={4}
        className="p-0 w-[var(--radix-popover-trigger-width)] min-w-[220px] bg-[var(--bt-surface)] border border-[var(--bt-border)] rounded-sm shadow-lg"
        onOpenAutoFocus={(e) => {
          // Let CommandInput auto-focus
          if (addingCustom) e.preventDefault();
        }}
      >
        {addingCustom ? (
          <div className="p-3 space-y-2" data-testid={testId ? `${testId}-add-custom-input-row` : undefined}>
            <p className="bt-label">Add custom pair</p>
            <div className="flex items-center gap-2">
              <Input
                autoFocus
                value={customInput}
                onChange={(e) => setCustomInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") { e.preventDefault(); handleAddCustom(); }
                  if (e.key === "Escape") { e.preventDefault(); setAddingCustom(false); setCustomInput(""); }
                }}
                placeholder="e.g. BTCINR"
                data-testid={testId ? `${testId}-add-custom-input` : undefined}
                className="bg-transparent border-[var(--bt-border)] h-9 rounded-sm uppercase"
              />
              <Button
                type="button"
                onClick={handleAddCustom}
                disabled={!customInput.trim()}
                data-testid={testId ? `${testId}-add-custom-confirm` : undefined}
                className="h-9 bg-[var(--bt-accent)] text-black hover:bg-[var(--bt-accent)]/90 rounded-sm"
              >
                <Check className="h-4 w-4" />
              </Button>
              <Button
                type="button"
                variant="ghost"
                onClick={() => { setAddingCustom(false); setCustomInput(""); }}
                data-testid={testId ? `${testId}-add-custom-cancel` : undefined}
                className="h-9 rounded-sm"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
            <p className="text-[11px] text-[var(--bt-text-muted)]">
              Enter your custom symbol — gets saved for this broker.
            </p>
          </div>
        ) : (
          <Command shouldFilter={true} className="bg-transparent">
            <CommandInput
              placeholder={searchPlaceholder}
              value={query}
              onValueChange={setQuery}
              data-testid={testId ? `${testId}-search-input` : undefined}
              className="h-10"
            />
            <CommandList className="max-h-64">
              <CommandEmpty>{emptyText}</CommandEmpty>
              <CommandGroup>
                {merged.map((o) => (
                  <CommandItem
                    key={o.value}
                    value={o.value}
                    onSelect={() => select(o.value)}
                    data-testid={testId ? `${testId}-option-${o.value}` : undefined}
                    className="cursor-pointer"
                  >
                    <span className="flex-1">{o.value}</span>
                    {o.custom && (
                      <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded-sm bg-[var(--bt-accent)]/15 text-[var(--bt-accent)] font-mono">
                        custom
                      </span>
                    )}
                    {value === o.value && (
                      <Check className="ml-2 h-4 w-4 text-[var(--bt-accent)]" />
                    )}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
            {showAddCustom && (
              <div className="border-t border-[var(--bt-border)]">
                <button
                  type="button"
                  onClick={() => setAddingCustom(true)}
                  data-testid={testId ? `${testId}-add-custom-trigger` : undefined}
                  className="w-full flex items-center gap-2 px-3 py-2.5 text-sm text-[var(--bt-accent)] hover:bg-[var(--bt-surface-hover)] transition-colors"
                >
                  <Plus className="h-4 w-4" />
                  <span>{addCustomLabel}</span>
                </button>
              </div>
            )}
          </Command>
        )}
      </PopoverContent>
    </Popover>
  );
}

export default SearchableSelect;
